import { useEffect, useState } from 'react';
import { Button, Dialog, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import type { RequestSelection } from './requests-screen';
import {
  DecisionControls,
  EMPTY_DECISION,
  type DecisionDraft,
  type DecisionOptions,
} from './decision-controls';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ DECIDING FROM THE LIST — the row menu's and the bulk bar's modal.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE ROW MENU USED TO DECIDE THE INSTANT IT WAS CLICKED, AND THAT IS WHY THIS EXISTS.**
 * *Elfogadás* in the kebab posted an approval with no note, no settlement and — after `0147` — no
 * overtime type, so the one decision `0147` moved to the approver was reachable only by opening the
 * request first. A menu item that silently takes every default is not a decision surface.
 *
 * ⚠️ **It is the same controls as the detail's rail**, from `decision-controls.tsx`, for the
 * reason that file records: two copies drift the moment one gains a field.
 *
 * ⚠️ **It serves a rejection too**, which has no settlement and no rate but does have a note —
 * *"miért nem"* is the thing a requester most wants to read, and until now no list could give it.
 *
 * ⚠️⚠️ **AND IT SERVES A BATCH, WHICH IS WHY ONE ANSWER COVERS MANY IDS.** `POST /overtime/decide`
 * takes one settlement and one type for every id in the call, so a batch asks the question once.
 * That is honest for the payout rate — an approver settling a weekend's claims is pricing a
 * weekend — and it is why the summary line says how many rows the answer will cover.
 */

export interface DecideRequest {
  /** The ids by kind — the screen splits them, because only it holds the page they came from. */
  readonly selection: RequestSelection;
  readonly decision: 'approved' | 'rejected';
}

export function DecideDialog({
  request,
  options,
  serverError,
  onClose,
  onConfirm,
}: {
  /** `null` closes it. */
  readonly request: DecideRequest | null;
  readonly options: DecisionOptions;
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onConfirm: (note: string, draft: DecisionDraft) => void;
}) {
  const t = useT();
  const [note, setNote] = useState('');
  const [draft, setDraft] = useState<DecisionDraft>(EMPTY_DECISION);

  /* ⚠️ Cleared when the dialog OPENS, not when it closes: a note left behind from the last batch
     would arrive on the next one, and closing is not always how it ends. */
  useEffect(() => {
    if (request === null) return;
    setNote('');
    setDraft(EMPTY_DECISION);
  }, [request]);

  const approving = request?.decision === 'approved';
  /* ⚠️ The settlement and the rate belong to an APPROVED claim. A rejection prices nothing and a
     leave request has no such choice, so in both cases the dialog is the note alone. */
  /* ⚠️ Whether the selection contains a CLAIM — a leave-only batch settles nothing. */
  const settles =
    request !== null && request.decision === 'approved' && request.selection.overtimeIds.length > 0;

  return (
    <Dialog
      open={request !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={approving ? t('leave.action.approve') : t('leave.action.reject')}
      description={t('leave.decide.count', {
        count:
          request === null
            ? 0
            : request.selection.leaveIds.length + request.selection.overtimeIds.length,
      })}
      width="min(460px, 94vw)"
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            /* ⚠️ The destructive fill is the kit's for a rejection, matching the bulk bar's own
               `destructive` action — the same act should not change colour with the surface. */
            variant={approving ? 'default' : 'destructive'}
            onClick={() => {
              onConfirm(note, draft);
            }}
          >
            {approving ? t('leave.action.approve') : t('leave.action.reject')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {!settles ? null : (
          <Field label={t('leave.settle.label')}>
            <DecisionControls draft={draft} options={options} onChange={setDraft} />
          </Field>
        )}

        <Field label={t('leave.detail.decisionNote')}>
          <TextInput
            value={note}
            placeholder={t('leave.detail.decisionNote')}
            aria-label={t('leave.detail.decisionNote')}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </Field>

        {serverError === null ? null : (
          <p className="text-destructive text-[12px]">{serverError}</p>
        )}
      </div>
    </Dialog>
  );
}
